import { useCallback, useMemo, useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  createEmptyContainerRow,
  createEmptyHandlingUnitRow,
  type PackingListUnitDefaults,
} from "@RequestFormV2/defaults/packingListDefaults";
import type { GoodCustomsInformation, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isCommodityRowEmpty, isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { isContainerRow } from "@RequestFormV2/sections/PackingList/utils/packingListSplit";
import {
  addCommodityToRow,
  addHandlingUnitRow,
  deleteCommodityFromRow,
  duplicateCommodityInRow,
  ensureCommodityKeys,
  ensureCommodityOnRow,
  ensureInitialCommodity,
  resolveCommodityTargetIndex,
} from "@RequestFormV2/sections/PackingList/utils/packingListMutations";
import { getCommodityFieldPath } from "@RequestFormV2/sections/PackingList/utils/fieldPaths";
import {
  applyPastedGrid,
  parseClipboardGrid,
  type PackingListPasteColumn,
} from "@RequestFormV2/sections/PackingList/utils/pastePackingList";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";
import {
  countPackingListRows,
  trackQuotePackingCommodityAdded,
  trackQuotePackingContainerAdded,
  trackQuotePackingHandlingUnitAdded,
} from "@RequestFormV2/formEvents/requestFormAnalytics";
import {
  canDeleteCommodity,
  canDeletePackingListRow,
  clonePackingListRow,
  findPackingListIndexByFieldId,
} from "@RequestFormV2/sections/PackingList/utils/packingListRowActions";

interface UsePackingListHandlersOptions {
  defaultCurrency?: string;
  unitDefaults?: PackingListUnitDefaults;
}

export function usePackingListHandlers({ defaultCurrency = "", unitDefaults }: UsePackingListHandlersOptions = {}) {
  const { control, getValues, setValue } = useFormContext();
  const persistDraft = usePersistDraft();

  const {
    fields: packingListFields,
    append,
    insert,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "packing_list",
  });

  const packingListFieldsRef = useRef(packingListFields);
  packingListFieldsRef.current = packingListFields;

  const isMutatingRowsRef = useRef(false);

  const beginRowMutation = useCallback(() => {
    isMutatingRowsRef.current = true;
  }, []);

  const endRowMutation = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isMutatingRowsRef.current = false;
      });
    });
  }, []);

  const resolvePackingListIndex = useCallback(
    (fieldId: string) => findPackingListIndexByFieldId(packingListFieldsRef.current, fieldId),
    [],
  );

  const getPackingListRows = useCallback(() => (getValues("packing_list") ?? []) as PackingListRow[], [getValues]);

  const setPackingListRows = useCallback(
    (rows: PackingListRow[]) => {
      replace(rows);
    },
    [replace],
  );

  const setRowCommodities = useCallback(
    (packingListIndex: number, rows: PackingListRow[]) => {
      setValue(
        `packing_list.${packingListIndex}.good_customs_information`,
        rows[packingListIndex]?.good_customs_information,
        {
          shouldDirty: true,
        },
      );
    },
    [setValue],
  );

  const appendContainer = useCallback(() => {
    const nextRow = createEmptyContainerRow(unitDefaults);
    const nextRows = [...getPackingListRows(), nextRow];
    append(nextRow);
    persistDraft("packing_list");
    trackQuotePackingContainerAdded(countPackingListRows(nextRows).total_containers);
  }, [append, getPackingListRows, persistDraft, unitDefaults]);

  const appendHandlingUnit = useCallback(() => {
    const nextRow = createEmptyHandlingUnitRow(unitDefaults);
    const nextRows = [...getPackingListRows(), nextRow];
    append(nextRow);
    persistDraft("packing_list");
    trackQuotePackingHandlingUnitAdded(countPackingListRows(nextRows).total_handling_units);
  }, [append, getPackingListRows, persistDraft, unitDefaults]);

  const handlePackingListPaste = useCallback(
    (anchorIndex: number, anchorColumn: PackingListPasteColumn, text: string) => {
      const grid = parseClipboardGrid(text);
      if (grid.length === 0) return;

      const nextRows = applyPastedGrid(getPackingListRows(), anchorIndex, anchorColumn, grid, () =>
        createEmptyHandlingUnitRow(unitDefaults),
      );

      replace(nextRows);
      persistDraft("packing_list");
    },
    [getPackingListRows, persistDraft, replace, unitDefaults],
  );

  const ensureCommodityOnRowHandler = useCallback(
    (packingListIndex: number) => {
      const nextRows = ensureCommodityOnRow(getPackingListRows(), packingListIndex, defaultCurrency);
      setRowCommodities(packingListIndex, nextRows);
    },
    [defaultCurrency, getPackingListRows, setRowCommodities],
  );

  const appendCommodity = useCallback(
    (packingListIndex?: number) => {
      const rows = getPackingListRows();
      const targetIndex = packingListIndex ?? resolveCommodityTargetIndex(rows);
      if (targetIndex === undefined) return;

      const nextRows = addCommodityToRow(rows, targetIndex, defaultCurrency);
      setRowCommodities(targetIndex, nextRows);
      trackQuotePackingCommodityAdded(countPackingListRows(nextRows).total_commodities);
    },
    [defaultCurrency, getPackingListRows, setRowCommodities],
  );

  const duplicateRow = useCallback(
    (fieldId: string) => {
      const index = findPackingListIndexByFieldId(packingListFieldsRef.current, fieldId);
      if (index === -1) return;

      const row = getValues(`packing_list.${index}`) as PackingListRow;
      insert(index + 1, clonePackingListRow(row));
      persistDraft("packing_list");
    },
    [getValues, insert, persistDraft],
  );

  const deleteRow = useCallback(
    (fieldId: string) => {
      const index = findPackingListIndexByFieldId(packingListFieldsRef.current, fieldId);
      if (index === -1) return;

      const rows = getPackingListRows();
      if (!canDeletePackingListRow(rows, index)) return;

      beginRowMutation();
      remove(index);
      endRowMutation();
      persistDraft("packing_list");
    },
    [beginRowMutation, endRowMutation, getPackingListRows, persistDraft, remove],
  );

  const duplicateCommodity = useCallback(
    (packingListIndex: number, commodityIndex: number) => {
      const nextRows = duplicateCommodityInRow(getPackingListRows(), packingListIndex, commodityIndex);
      setRowCommodities(packingListIndex, nextRows);
      persistDraft("packing_list");
    },
    [getPackingListRows, persistDraft, setRowCommodities],
  );

  const deleteCommodity = useCallback(
    (packingListIndex: number, commodityIndex: number) => {
      const rows = getPackingListRows();
      if (!canDeleteCommodity(rows)) return;

      beginRowMutation();
      const nextRows = deleteCommodityFromRow(rows, packingListIndex, commodityIndex);
      setRowCommodities(packingListIndex, nextRows);
      endRowMutation();
      persistDraft("packing_list");
    },
    [beginRowMutation, endRowMutation, getPackingListRows, persistDraft, setRowCommodities],
  );

  const maybeAppendTrailingRow = useCallback(
    (index: number) => {
      if (isMutatingRowsRef.current) return;

      const rows = getPackingListRows();
      const row = rows[index];
      if (!row || isPackingListRowEmpty(row)) return;

      const isContainer = isContainerRow(row);
      const sameKindRows = rows
        .map((item, rowIndex) => ({ item, rowIndex }))
        .filter(({ item }) => isContainerRow(item) === isContainer);

      const lastSameKind = sameKindRows[sameKindRows.length - 1];
      if (lastSameKind?.rowIndex !== index) return;

      if (isContainer) {
        appendContainer();
      } else {
        appendHandlingUnit();
      }
    },
    [appendContainer, appendHandlingUnit, getPackingListRows],
  );

  const maybeAppendTrailingCommodity = useCallback(
    (packingListIndex: number, commodityIndex: number) => {
      if (isMutatingRowsRef.current) return;

      const commodities = getValues(`packing_list.${packingListIndex}.good_customs_information`) ?? [];
      const commodity = commodities[commodityIndex];
      if (isCommodityRowEmpty(commodity)) return;
      if (commodityIndex !== commodities.length - 1) return;
      appendCommodity(packingListIndex);
    },
    [appendCommodity, getValues],
  );

  const handlePackingListFieldBlur = useCallback(
    (fieldId: string) => {
      const index = resolvePackingListIndex(fieldId);
      if (index === -1) return;

      maybeAppendTrailingRow(index);
    },
    [maybeAppendTrailingRow, resolvePackingListIndex],
  );

  const handleCommodityFieldBlur = useCallback(
    (packingListIndex: number, commodityIndex: number) => {
      maybeAppendTrailingCommodity(packingListIndex, commodityIndex);
    },
    [maybeAppendTrailingCommodity],
  );

  const moveCommodityToParent = useCallback(
    (packingListIndex: number, commodityIndex: number, toPackingListIndex: number) => {
      const commodity = getValues(`packing_list.${packingListIndex}.good_customs_information.${commodityIndex}`) as
        | GoodCustomsInformation
        | undefined;
      if (!commodity) return;

      setValue(getCommodityFieldPath(packingListIndex, commodityIndex, "parent_assigned"), true, {
        shouldDirty: true,
      });

      if (toPackingListIndex !== packingListIndex) {
        setValue(getCommodityFieldPath(packingListIndex, commodityIndex, "parent_packing_list_index"), toPackingListIndex, {
          shouldDirty: true,
        });
      } else {
        setValue(getCommodityFieldPath(packingListIndex, commodityIndex, "parent_packing_list_index"), undefined, {
          shouldDirty: true,
        });
      }

      maybeAppendTrailingCommodity(packingListIndex, commodityIndex);
    },
    [getValues, maybeAppendTrailingCommodity, setValue],
  );

  const ensureInitialRows = useCallback(() => {
    let rows = ensureCommodityKeys(getPackingListRows());
    const hasHandlingUnit = rows.some((row) => !isContainerRow(row));
    const hasContainer = rows.some(isContainerRow);

    // Seed a blank HU only for an empty packing list. Never re-inject an HU onto container-only lists.
    if (!hasHandlingUnit && !hasContainer) {
      rows = addHandlingUnitRow(rows, unitDefaults);
    }

    setPackingListRows(ensureInitialCommodity(rows, defaultCurrency));
  }, [defaultCurrency, getPackingListRows, setPackingListRows, unitDefaults]);

  return useMemo(
    () => ({
      packingListFields,
      appendContainer,
      appendHandlingUnit,
      appendCommodity,
      duplicateRow,
      deleteRow,
      duplicateCommodity,
      deleteCommodity,
      ensureCommodityOnRow: ensureCommodityOnRowHandler,
      handlePackingListFieldBlur,
      handlePackingListPaste,
      handleCommodityFieldBlur,
      ensureInitialRows,
      moveCommodityToParent,
      setPackingListValue: setValue,
    }),
    [
      packingListFields,
      appendContainer,
      appendHandlingUnit,
      appendCommodity,
      duplicateRow,
      deleteRow,
      duplicateCommodity,
      deleteCommodity,
      ensureCommodityOnRowHandler,
      handlePackingListFieldBlur,
      handlePackingListPaste,
      handleCommodityFieldBlur,
      ensureInitialRows,
      moveCommodityToParent,
      setValue,
    ],
  );
}
